import { Calendar, ChevronDown, Search } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';

// Sample list items for approval workflow (with detail fields for left panel)
const MOCK_ITEMS = [
  { id: '1', date: '12-1-2026', heading: 'Heading', time: '8:00 Am', status: 'Status', name: 'John', mail: 'john@example.com', mobileNumber: '+1 234 567 8901' },
  { id: '2', date: '12-1-2026', heading: 'Heading', time: '8:00 Am', status: 'Status', name: 'Jane Smith', mail: 'jane.smith@example.com', mobileNumber: '+1 234 567 8902' },
  { id: '3', date: '12-1-2026', heading: 'Heading', time: '8:00 Am', status: 'Status', name: 'Alex ', mail: 'alex@example.com', mobileNumber: '+1 234 567 8903' },
  { id: '4', date: '12-1-2026', heading: 'Heading', time: '8:00 Am', status: 'Status', name: 'Sam ', mail: 'sam@example.com', mobileNumber: '+1 234 567 8904' },
  { id: '5', date: '12-1-2026', heading: 'Heading', time: '8:00 Am', status: 'Status', name: 'Chris ', mail: 'chris@example.com', mobileNumber: '+1 234 567 8905' },
  { id: '6', date: '12-1-2026', heading: 'Heading', time: '8:00 Am', status: 'Status', name: 'sneha', mail: 'sneha@example.com', mobileNumber: '+1 234 567 8906' },
  { id: '7', date: '12-1-2026', heading: 'Heading', time: '8:00 Am', status: 'Status', name: 'vamshi', mail: 'vamshi@example.com', mobileNumber: '+1 234 567 8907' },
];

const TYPE_OPTIONS = ['All', 'Facility', 'Leave', 'Other'];

const INPUT_BASE_CLASS = 'w-full rounded-[10px] bg-white/80 border border-gray-200 px-3 py-2 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400';

const ApprovalWorkflow = () => {
  // Log screen view
  useScreenLogging('ApprovalWorkflow', {
    component: 'ApprovalWorkflow',
    screenType: 'approval-workflow',
  });

  const [items, setItems] = useState(MOCK_ITEMS);
  const [selectedType, setSelectedType] = useState('');
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('1');
  const [detailName, setDetailName] = useState('');
  const [detailMail, setDetailMail] = useState('');
  const [detailMobile, setDetailMobile] = useState('');
  const [detailReason, setDetailReason] = useState('');
  const typeDropdownRef = useRef(null);

  const selectedItem = items.find((item) => item.id === selectedItemId);

  useEffect(() => {
    if (selectedItem) {
      setDetailName(selectedItem.name ?? '');
      setDetailMail(selectedItem.mail ?? '');
      setDetailMobile(selectedItem.mobileNumber ?? '');
      setDetailReason('');
    }
  }, [selectedItem]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setIsTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayType = selectedType || 'Select type';

  const panelStyle = {
    background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
    boxShadow: '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(30px)',
  };

  return (
    <div className="flex w-full h-full gap-4">
      {/* Left panel – details when a card is selected (name, mail, mobile, reason, decline/approve) */}
      <div
        className="w-full max-w-[320px] flex flex-col rounded-xl overflow-hidden flex-shrink-0"
        style={panelStyle}
      >
        {selectedItem ? (
          <div className="p-4 flex flex-col gap-3 flex-1 min-h-0 overflow-y-auto">
            <label className="block text-sm text-gray-500">Name</label>
            <input
              type="text"
              value={detailName}
              onChange={(e) => setDetailName(e.target.value)}
              placeholder="Name"
              className={INPUT_BASE_CLASS}
            />
            <label className="block text-sm text-gray-500">Mail</label>
            <input
              type="email"
              value={detailMail}
              onChange={(e) => setDetailMail(e.target.value)}
              placeholder="Mail"
              className={INPUT_BASE_CLASS}
            />
            <label className="block text-sm text-gray-500">Mobile number</label>
            <input
              type="text"
              value={detailMobile}
              onChange={(e) => setDetailMobile(e.target.value)}
              placeholder="Mobile number"
              className={INPUT_BASE_CLASS}
            />
            <label className="block text-sm text-gray-500">Reason</label>
            <textarea
              value={detailReason}
              onChange={(e) => setDetailReason(e.target.value)}
              placeholder="Enter Reason"
              rows={3}
              className={`${INPUT_BASE_CLASS} min-h-[60px] resize-y`}
            />
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {}}
                className="flex-1 py-2.5 px-4 rounded-[10px] bg-red-600 hover:bg-red-700 text-white font-medium text-base transition-colors"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={() => {}}
                className="flex-1 py-2.5 px-4 rounded-[10px] bg-green-600 hover:bg-green-700 text-white font-medium text-base transition-colors"
              >
                Approve
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 p-4 flex items-center justify-center text-gray-500 text-center">
            Select a card to view details
          </div>
        )}
      </div>

      {/* Right panel – filters and list of cards */}
      <div
        className="flex-1 min-w-0 max-w-[380px] flex flex-col rounded-xl overflow-hidden"
        style={panelStyle}
      >
        {/* Top row: Select type dropdown + Calendar button */}
        <div className="p-4 flex items-center gap-2 flex-shrink-0">
          <div className="relative flex-1" ref={typeDropdownRef}>
            <button
              type="button"
              onClick={() => setIsTypeDropdownOpen((o) => !o)}
              className="w-full h-11 pl-4 pr-3 flex items-center justify-between rounded-[10px] text-left text-gray-700 bg-white/80 border border-gray-200 shadow-sm hover:bg-white/90 transition-colors"
            >
              <span className="text-base font-normal truncate">
                {displayType}
              </span>
              <ChevronDown
                className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${isTypeDropdownOpen ? 'rotate-180' : ''}`}
                strokeWidth={2}
              />
            </button>
            {isTypeDropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-1 py-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                {TYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      setSelectedType(opt === 'All' ? '' : opt);
                      setIsTypeDropdownOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-gray-700 hover:bg-gray-100 text-sm"
                  >
                    {opt === 'All' ? 'Select type' : opt}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            className="h-11 w-11 flex items-center justify-center rounded-[10px] bg-white/80 border border-gray-200 shadow-sm hover:bg-white/90 transition-colors text-gray-600"
            aria-label="Open calendar"
          >
            <Calendar className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>

        {/* Search by */}
        <div className="px-4 pb-3 flex-shrink-0">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-11 pl-10 pr-4 rounded-[10px] text-gray-700 bg-white/80 border border-gray-200 shadow-sm placeholder-gray-400 text-base focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400"
            />
          </div>
        </div>

        {/* Scrollable list of items */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-2">
          {items.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">
              No items to display
            </div>
          ) : (
            items.map((item) => {
              const isSelected = item.id === selectedItemId;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSelectedItemId(item.id)}
                  className={`w-full text-left p-3 rounded-xl border transition-colors ${
                    isSelected
                      ? 'bg-teal-50/80 border-teal-200/80 shadow-sm'
                      : 'bg-white/60 border-gray-200/80 hover:bg-white/80'
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <span className="text-sm text-gray-600">{item.date ?? ''}</span>
                    <span className="text-sm text-gray-600">{item.time ?? ''}</span>
                  </div>
                  <div className="flex justify-between items-start gap-2 mt-1">
                    <span className="text-base text-gray-800 font-medium">{item.heading ?? ''}</span>
                    <span className="text-sm text-gray-600">{item.status ?? ''}</span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalWorkflow;
